import {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useTranslation } from "react-i18next";
import uploadCloud from "../../assets/upload_cloud.svg";
import uploadArrow from "../../assets/upload_arrow.svg";
import uploadError from "../../assets/upload_error.svg";
import "./pdfController.scss";
import { PdfControllerProps } from "./types";
import "./i18n";
import { getDocument } from 'pdfjs-dist';
import { jsPDF } from "jspdf";
import { Button } from "@pickit/pickit-components";

const PdfController = forwardRef(
  (
    {
      children,
      fileUploaded,
      showError,
      errorMessage,
      title,
      buttonText,
      disabled,
      loading,
      goToPreviousFile,
      fileHandler,
      testId,
      accept = ".png,.jpg,.pdf",
      processAllPages = false,
      onFileTypeError,
    }: PdfControllerProps,
    ref
  ) => {
    const { t } = useTranslation();
    const dropRef = useRef<HTMLDivElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);
    const dragCounter = useRef<number>(0);
    const [processingFile, setProcessingFile] = useState(false);

    const openFileReader = (
      e?: React.MouseEvent<HTMLButtonElement> | undefined
    ): void => {
      e?.preventDefault();
      e?.stopPropagation();
      if (fileRef.current) fileRef.current.click();
    };


    // Evitar la funcionalidad por defecto. Abre el dropped file
    const handleDrag = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragIn = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Si dentro del contenedor de "Drag and Drop" hay muchos childs,
      // se va a disparar este evento por cada uno de ellos.
      dragCounter.current = dragCounter.current + 1;

      // En caso que el evento contenga algun archivo
      if (e?.dataTransfer?.items && e.dataTransfer?.items?.length > 0) {
        setDragging(true);
      }
    };
    const handleDragOut = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounter.current = dragCounter.current - 1;

      // Solo se suelta el draggging cuando se sale del ultimo elemento
      // ya que dicho container puede tener muchos child y dicho evento
      // es lanzado cada vez que sale de cada uno de los child.
      if (dragCounter.current === 0) setDragging(false);
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      if (e?.dataTransfer?.files && e?.dataTransfer.files.length > 0) {
        dragCounter.current = 0;
        handleFileChange(e.dataTransfer.files[0]);
        e.dataTransfer.clearData();
      }
    };

    const hasError = () => showError && !dragging && !loading;
    const showFile = () => fileUploaded && !dragging && !loading && !showError;

    useImperativeHandle(ref, () => ({
      triggerOnChange: () => openFileReader(),
    }));

    useEffect(() => {
      const div = dropRef.current;

      if (!div || disabled) return;

      div.addEventListener("dragenter", handleDragIn);
      div.addEventListener("dragleave", handleDragOut);
      div.addEventListener("dragover", handleDrag);
      div.addEventListener("drop", handleDrop);

      return () => {
        div.removeEventListener("dragenter", handleDragIn);
        div.removeEventListener("dragleave", handleDragOut);
        div.removeEventListener("dragover", handleDrag);
        div.removeEventListener("drop", handleDrop);
      };

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);

    const rootContainerClasses = [
      "pdf-root-container",
      showFile() && "pdf-root-container-border",
    ].join(" ");

    const containerClasses = [
      "pdf-container-idle",
      dragging && "pdf-container-dragging",
      (loading || processingFile) && "pdf-container-loading",
      hasError() && "pdf-container-error",
      showFile() && "pdf-container-hidden",
    ].join(" ");


    const [, setImageSrc] = useState<string | null>(null);
    const [, setPdfSrc] = useState<string | undefined>(undefined);

    const handleFileEventChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      handleFileChange(file);
    }
    
    const handleFileChange = async (file: File | undefined) => {
      if (!file) {
        if (onFileTypeError && showError) {
          onFileTypeError("");
        }
        return;
      }
      const fileType = file.type;
      
      if (accept === ".pdf" && fileType !== "application/pdf") {
        const errorMsg = "El formato del archivo debe ser PDF y no puede superar los 5MB.";
        if (onFileTypeError) {
          onFileTypeError(errorMsg);
        }
        return;
      }
      
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (fileType !== "application/pdf" && file.size > maxSize) {
        const errorMsg = "El archivo no puede superar los 5MB.";
        if (onFileTypeError) {
          onFileTypeError(errorMsg);
        }
        return;
      }
      
      if (fileType === "application/pdf") {
        try {
          setProcessingFile(true);
          const arrayBuffer = await file.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
      
          const pdfDoc = await getDocument({ data: uint8Array }).promise;
      
          if (pdfDoc.numPages >= 1) {
            const compressImage = (canvas: HTMLCanvasElement, quality: number = 0.85): string => {
              return canvas.toDataURL('image/jpeg', quality);
            };
            
            const processPage = async (pageNumber: number, pdfDoc: any, pdf: jsPDF, scale: number, quality: number, isFirstPage: boolean = false): Promise<string> => {
              const page = await pdfDoc.getPage(pageNumber);
              const viewport = page.getViewport({ scale });
              
              if (!viewport || !viewport.width || !viewport.height) {
                throw new Error('Error');
              }
              
              const canvas = document.createElement('canvas');
              const context = canvas.getContext('2d');
              canvas.width = viewport.width;
              canvas.height = viewport.height;
              
              if (context) {
                await page.render({ canvasContext: context, viewport }).promise;
              } else {
                throw new Error('Error');
              }
              
              const imageUrl = compressImage(canvas, quality);
              
              canvas.width = 0;
              canvas.height = 0;
              
              if (isFirstPage) {
                setImageSrc(imageUrl);
              }
              
              return imageUrl;
            };
            
            const createProcessedPdf = async (scale: number, quality: number): Promise<Blob> => {
              const pdf = new jsPDF();
              
              if (processAllPages) {
                for (let pageNumber = 1; pageNumber <= pdfDoc.numPages; pageNumber++) {
                  const imageUrl = await processPage(pageNumber, pdfDoc, pdf, scale, quality, pageNumber === 1);
                  
                  const imgProps = pdf.getImageProperties(imageUrl);
                  const pdfWidth = pdf.internal.pageSize.getWidth();
                  const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
                  
                  if (pageNumber > 1) {
                    pdf.addPage();
                  }
                  
                  pdf.addImage(imageUrl, "JPEG", 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
                  
                  if (pageNumber % 2 === 0) {
                    await new Promise(resolve => setTimeout(resolve, 0));
                  }
                }
              } else {
                const imageUrl = await processPage(1, pdfDoc, pdf, scale, quality, true);
                
                const imgProps = pdf.getImageProperties(imageUrl);
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
                pdf.addImage(imageUrl, "JPEG", 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
              }
              
              return pdf.output("blob");
            };
            
            let pdfBlob: Blob;
            let pdfFile: File;
            let scale = 1.5;
            let quality = 0.85;
            
            if (file.size > 2 * 1024 * 1024) {
              scale = 1.2;
              quality = 0.75;
            }
            
            pdfBlob = await createProcessedPdf(scale, quality);
            
            if (pdfBlob.size > maxSize) {
              scale = 1.0;
              quality = 0.65;
              pdfBlob = await createProcessedPdf(scale, quality);
              
              if (pdfBlob.size > maxSize) {
                scale = 0.8;
                quality = 0.5;
                pdfBlob = await createProcessedPdf(scale, quality);
              }
            }
            
            if (pdfBlob.size > maxSize) {
              const errorMsg = "El formato del archivo debe ser PDF y no puede superar los 5MB.";
              setProcessingFile(false);
              if (onFileTypeError) {
                onFileTypeError(errorMsg);
              }
              return;
            }
            
            const pdfUrl = URL.createObjectURL(pdfBlob);
            setPdfSrc(pdfUrl);

            pdfFile = new File([pdfBlob], file.name, { type: "application/pdf" });

            setProcessingFile(false);
            if (onFileTypeError) {
              onFileTypeError("");
            }
            fileHandler(pdfFile);

          } else {
            setProcessingFile(false);
            if (onFileTypeError) {
              onFileTypeError("");
            }
            fileHandler(file);
          }
        } catch (error) {
          console.error('Error:', error);
          setProcessingFile(false);
        }
      }
      else if (fileType === "image/png" || fileType === "image/jpg") {
        if (onFileTypeError) {
          onFileTypeError("");
        }
        fileHandler(file);
      } else {
        if (accept !== ".pdf") {
          fileHandler(file);
        } else {
          return;
        }
      }
    };

    return (
      <div className={rootContainerClasses} data-testId="terms-and-conditions-file-input">
        {showFile() && <div className="pdf-children-container">{children}</div>}
        <div ref={dropRef} className={containerClasses}>
          <div className="pdf-icon-container">
            {hasError() ? (
              <img
                className="pdf-upload-error-icon"
                src={uploadError}
                alt="upload-error-icon"
              />
            ) : (
              <div className="pdf-upload-icon">
                <img
                  className="pdf-upload-cloud"
                  src={uploadCloud}
                  alt="upload-icon"
                />
                <img
                  className="pdf-upload-arrow"
                  src={uploadArrow}
                  alt="upload-icon"
                />
              </div>
            )}
            <p className="pdf-title" data-testId={`${testId}-pdf-title`}>{dragging ? t("pdf:dragging") : title}</p>
          </div>
          <div className="pdf-content">
            {fileUploaded && showError ? (
              <Button
                disabled={disabled}
                onClick={() => goToPreviousFile && goToPreviousFile()}
                testId={`${testId}-previous-file`}
                size="medium"
                width="220"
                hierarchy="primary"
              />
            ) : (
              <Button
                onClick={() => openFileReader()}
                testId={`${testId}-open-file-reader`}
                label={buttonText}
                size="medium"
                width="220"
                hierarchy="primary"
              />
            )}
            <p className="pdf-message" data-testId={`${testId}-pdf-instruction`}>{t("pdf:instruction")}</p>
          </div>
          {(loading || processingFile) && (
            <div className="pdf-loading-container">
              <div className="pdf-loading-bar"></div>
            </div>
          )}
        </div>
        {hasError() && <p className="pdf-error-message" data-testId="pdf-error-message">{errorMessage}</p>}
          <input 
            type="file"
            ref={fileRef}
            className="pdf-hidden"
            accept={accept}
            onChange={handleFileEventChange}
          />
      </div>
    );
  }
);

export default PdfController;

